/**
 * The size ceiling on file uploads, and why it is what it is.
 *
 * `cynque-api-server` pins Litestar's `request_max_body_size` per route layer
 * (cynque-api-server #1563, PR #1566): 10,000,000 bytes by default
 * (`REQUEST_MAX_BODY_BYTES`), and `UPLOAD_MAX_BODY_BYTES` -- 25,000,000 -- on
 * the document POSTs, set once in `make_post_document` so every document type
 * shares it. That second number is the one this file tracks. It is the API's
 * DEFAULT: the setting is env-overridable, so a deployment that overrides it
 * needs this constant changed with it. (Case and email replies have a cap of
 * their own, `REPLY_MAX_BODY_BYTES`, and a client guard of their own in
 * caseReplyAttachments.ts; nothing here applies to them.)
 *
 * Litestar checks the `content-length` HEADER and raises
 * `RequestEntityTooLarge` before reading a single byte, so an oversized upload
 * is rejected only after the whole file has crossed the wire twice -- once to
 * the webapp dyno, which buffers it entirely in memory
 * (server/middleware/20-proxy.ts), and again on to the API. Hence the client
 * guard at pick time. Production faults #133922044 / #133922043, 2026-08-29: a
 * ~19.1 MB contract document, `content-length: 20,010,831`, refused when the cap
 * was 10 MB.
 *
 * ⚠ THE API'S LIMIT IS ON THE WHOLE MULTIPART BODY, NOT ON THE FILE. `buildPostBody`
 * (services/http/buildPostBody.ts) sends the file as one part and bundles every
 * other field into a JSON `payload` part -- descr, documentType, the SHA-256
 * checksum, the parent and silo references -- and those bytes, plus the multipart
 * boundaries and part headers, are all charged against the same cap. So a file of
 * exactly `API_UPLOAD_MAX_BODY_BYTES` still gets a 413. The client cap must
 * therefore sit BELOW the API cap, with room for the envelope.
 *
 * 500 KB of headroom is far more than any realistic envelope (a few KB) and buys
 * a round number for the message a user reads: "24.5 MB".
 *
 * ⚠ Raising this further raises what the proxy holds in dyno memory per
 * concurrent upload. Past this size, fix that (cynque-api-server #1567, uploads
 * straight to object storage) rather than budget around it.
 */

/** The API's `UPLOAD_MAX_BODY_BYTES` default, in bytes. */
export const API_UPLOAD_MAX_BODY_BYTES = 25_000_000

/**
 * Bytes reserved for the multipart envelope (boundaries, part headers, and the
 * JSON `payload` part) so a file that passes the client guard cannot be rejected
 * upstream on overhead alone.
 */
export const MULTIPART_ENVELOPE_ALLOWANCE_BYTES = 500_000

/** The largest single file the client will accept for upload. */
export const MAX_UPLOAD_FILE_BYTES =
  API_UPLOAD_MAX_BODY_BYTES - MULTIPART_ENVELOPE_ALLOWANCE_BYTES
